import { useId } from "react";
import { cn } from "@/lib/utils";
import logoFrame from "@/assets/logo-frame.svg";

interface LogoSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Diamètre. Un nombre est interprété en px ; une chaîne accepte n'importe quelle
   * longueur CSS, y compris un `clamp()` qui s'adapte au layout — c'est ce que fait
   * le voile de chargement plein écran. 24 px minimum.
   */
  size?: number | string;
  /** Durée d'un tour de balle. */
  speed?: string;
  label?: string;
}

export function LogoSpinner({
  size = 48,
  speed = "1.25s",
  label = "Chargement…",
  className,
  ...props
}: LogoSpinnerProps) {
  const clip = useId();
  const dim = typeof size === "number" ? `${size}px` : size;
  return (
    <div
      role="status"
      aria-label={label}
      className={cn("relative shrink-0", className)}
      style={{ width: dim, height: dim }}
      {...props}
    >
      <img src={logoFrame} alt="" className="h-full w-full" />
      {/*
        Le centrage passe par `inset-0` + marges auto, JAMAIS par `-translate-*`.
        `animate-spin` anime la propriété `transform` : un `translate(-50%,-50%)`
        posé là serait écrasé par la rotation. Et comme `@keyframes spin` n'a
        qu'une étape `to`, l'état de départ interpolé garde la translation — la
        balle part donc centrée puis DÉRIVE vers le bas à droite au fil du tour,
        ce qu'une capture statique à t=0 ne montre pas. Les marges auto centrent
        sans toucher à `transform`, donc l'axe de rotation reste le centre.
      */}
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="absolute inset-0 m-auto motion-safe:animate-spin motion-reduce:animate-none"
        style={{ width: "26.8%", height: "26.8%", animationDuration: speed }}
      >
        <defs>
          <clipPath id={clip}>
            <circle cx="12" cy="12" r="12" />
          </clipPath>
        </defs>
        <circle cx="12" cy="12" r="12" fill="hsl(var(--accent))" />
        <g clipPath={`url(#${clip})`} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.4">
          <circle cx="-7" cy="12" r="13" />
          <circle cx="31" cy="12" r="13" />
        </g>
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  );
}
